import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Film, Send, Plus, Trash2, Loader2, Calendar, MapPin,
  Globe, ChevronLeft, X, Check,
} from "lucide-react";

const WINDOW_LABELS = {
  theatrical:     "Theatrical",
  home_video:     "Home Video / VDO",
  satellite_ott:  "Satellite · TV · OTT",
  overseas:       "Overseas",
  music:          "Music",
  non_theatrical: "Non-Theatrical",
};

const ACCENT = "#0F766E";

/**
 * L13 · Distributor Slate — full page.
 * Lists every film in the distributor's slate. Each film row expands
 * to show its release windows (kind × territory × dates × exclusivity).
 * Producers, exhibitors and sales agents will read this via the public
 * endpoint in later sprints.
 */
export default function DistributorSlate() {
  const [org, setOrg] = useState(null);
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    api.get("/onboard/me/organization")
      .then(({ data }) => setOrg(data.organization))
      .catch(() => setOrg(null));
    refresh();
  }, []);

  const refresh = () => {
    setLoading(true);
    api.get("/distributor/slate/mine")
      .then(({ data }) => setFilms(data.films || []))
      .catch(() => setFilms([]))
      .finally(() => setLoading(false));
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="distributor-slate-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-white transition"
            data-testid="distributor-slate-back">
        <ChevronLeft size={13}/> Back to Dashboard
      </Link>

      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">L13 · Distributor</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
              style={{ color: ACCENT }}
              data-testid="distributor-slate-heading">
            Sales Catalogue
          </h1>
          <p className="mt-3 text-sm text-white/60 max-w-xl leading-relaxed">
            Every film you carry, window by window. Add release dates and
            territory locks so producers, exhibitors and sales agents
            know what you can license.
          </p>
        </div>
        <Button
          className="h-10 rounded-full text-[11px] uppercase tracking-[0.22em] bg-teal-600 text-white hover:bg-teal-500"
          onClick={() => setShowAdd(true)}
          data-testid="distributor-slate-add-btn">
          <Plus size={13} className="mr-1.5"/> Add Film
        </Button>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/55"
               data-testid="distributor-slate-loading">
            <Loader2 size={16} className="animate-spin inline mr-2"/> Loading slate…
          </div>
        ) : films.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center"
               data-testid="distributor-slate-empty">
            <Film size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No films on your slate yet.</div>
            <div className="text-white/45 text-[11px] mt-2 max-w-md mx-auto">
              Add every film you have rights to — one row per film, one entry
              per release window. Exhibitors and sales agents discover you
              via this catalogue.
            </div>
          </div>
        ) : (
          <div className="grid gap-4"
               data-testid="distributor-slate-list">
            {films.map((f) => (
              <FilmRow key={f.id} film={f} onDelete={refresh}/>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddFilmModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
function FilmRow({ film, onDelete }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!confirm(`Remove "${film.title}" from your slate?`)) return;
    setBusy(true);
    try {
      await api.delete(`/distributor/slate/${film.id}`);
      toast.success("Film removed.");
      onDelete();
    } catch {
      toast.error("Could not remove film.");
    } finally { setBusy(false); }
  };

  return (
    <div className="cs-card rounded-xl p-5"
         data-testid={`distributor-slate-film-${film.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        {film.poster_url ? (
          <img src={film.poster_url} alt={film.title}
               className="w-16 h-20 rounded-md object-cover border border-white/10"/>
        ) : (
          <div className="w-16 h-20 rounded-md bg-teal-500/15 text-teal-300 flex items-center justify-center">
            <Film size={22}/>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg uppercase tracking-tight">{film.title}</div>
            {film.year && (
              <span className="px-1.5 py-0.5 rounded-full bg-white/8 text-[9px] tracking-[0.16em] uppercase text-white/60">
                {film.year}
              </span>
            )}
            {film.language && (
              <span className="px-1.5 py-0.5 rounded-full bg-white/8 text-[9px] tracking-[0.16em] uppercase text-white/60">
                {film.language}
              </span>
            )}
          </div>
          {film.producer && (
            <div className="mt-1 text-xs text-white/55">Producer · {film.producer}</div>
          )}
          {film.notes && (
            <div className="mt-2 text-[11px] text-white/50 leading-relaxed">{film.notes}</div>
          )}
        </div>
        <button onClick={remove} disabled={busy}
                className="text-white/40 hover:text-red-400 transition p-1"
                data-testid={`distributor-slate-delete-${film.id}`}>
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
        </button>
      </div>

      {film.windows?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/8 grid sm:grid-cols-2 lg:grid-cols-3 gap-2"
             data-testid={`distributor-slate-windows-${film.id}`}>
          {film.windows.map((w, i) => (
            <div key={i}
                 className="rounded-lg bg-white/4 border border-white/8 p-2.5"
                 data-testid={`distributor-slate-window-${film.id}-${i}`}>
              <div className="flex items-center gap-1.5">
                <span className="px-1.5 py-0.5 rounded-full bg-teal-500/20 text-teal-200 text-[9px] tracking-[0.16em] uppercase">
                  {WINDOW_LABELS[w.kind] || w.kind}
                </span>
                {w.exclusive && (
                  <span className="text-[9px] tracking-[0.16em] uppercase text-emerald-300">EXCL</span>
                )}
              </div>
              <div className="mt-1.5 text-[11px] text-white/75 flex items-center gap-1">
                <MapPin size={10} className="text-white/40"/> {w.territory}
              </div>
              {(w.start_date || w.end_date) && (
                <div className="mt-0.5 text-[10px] text-white/50 flex items-center gap-1">
                  <Calendar size={9} className="text-white/40"/>
                  {w.start_date || "?"} → {w.end_date || "open"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
function AddFilmModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", year: "", language: "", producer: "",
    poster_url: "", notes: "",
  });
  const [windows, setWindows] = useState([
    { kind: "theatrical", territory: "", start_date: "", end_date: "", exclusive: true },
  ]);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addWindow = () => setWindows((w) => [...w, {
    kind: "theatrical", territory: "", start_date: "", end_date: "", exclusive: true,
  }]);
  const rmWindow = (i) => setWindows((w) => w.filter((_, idx) => idx !== i));
  const updWindow = (i, k, v) => setWindows((w) =>
    w.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const submit = async () => {
    if (form.title.trim().length < 1) return toast.error("Title is required.");
    const cleanWindows = windows.filter((w) => w.territory.trim().length > 0);
    if (cleanWindows.length === 0) return toast.error("Add at least one release window with a territory.");
    setBusy(true);
    try {
      await api.post("/distributor/slate", {
        title:      form.title.trim(),
        year:       form.year ? Number(form.year) : null,
        language:   form.language.trim(),
        producer:   form.producer.trim(),
        poster_url: form.poster_url.trim(),
        notes:      form.notes.trim(),
        windows:    cleanWindows,
      });
      toast.success("Film added to slate.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add film.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="distributor-slate-add-modal">
      <div className="w-full max-w-3xl cs-card rounded-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500/15 text-teal-300 flex items-center justify-center">
              <Film size={18}/>
            </div>
            <div>
              <div className="font-display text-lg uppercase tracking-tight">Add Film to Slate</div>
              <div className="text-[11px] text-white/55">One row per film · add each window separately below.</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1">
            <X size={16}/>
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Title *">
            <Input value={form.title} onChange={set("title")} className="bg-black/40 border-white/15"
                   data-testid="distributor-slate-title"/>
          </Field>
          <Field label="Year">
            <Input value={form.year} onChange={set("year")} type="number" min={1900} max={2100}
                   placeholder="2025" className="bg-black/40 border-white/15"
                   data-testid="distributor-slate-year"/>
          </Field>
          <Field label="Language">
            <Input value={form.language} onChange={set("language")} placeholder="Hindi, Tamil, English…"
                   className="bg-black/40 border-white/15"
                   data-testid="distributor-slate-language"/>
          </Field>
          <Field label="Producer">
            <Input value={form.producer} onChange={set("producer")} placeholder="e.g. YRF, Dharma Productions"
                   className="bg-black/40 border-white/15"
                   data-testid="distributor-slate-producer"/>
          </Field>
          <Field label="Poster URL">
            <Input value={form.poster_url} onChange={set("poster_url")} placeholder="https://…"
                   className="bg-black/40 border-white/15"
                   data-testid="distributor-slate-poster"/>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Notes">
            <Textarea value={form.notes} onChange={set("notes")} className="bg-black/40 border-white/15 min-h-[70px]"
                      data-testid="distributor-slate-notes"/>
          </Field>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Release Windows *</Label>
            <Button size="sm" variant="ghost" onClick={addWindow}
                    className="h-8 rounded-full text-[10px] tracking-[0.2em] uppercase border border-white/15 hover:bg-white/5 text-white/75"
                    data-testid="distributor-slate-add-window">
              <Plus size={11} className="mr-1"/> Add Window
            </Button>
          </div>
          <div className="space-y-3" data-testid="distributor-slate-windows-editor">
            {windows.map((w, i) => (
              <div key={i} className="rounded-lg bg-black/25 border border-white/8 p-3"
                   data-testid={`distributor-slate-window-row-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Window {i + 1}</div>
                  {windows.length > 1 && (
                    <button onClick={() => rmWindow(i)}
                            className="text-white/40 hover:text-red-400 p-1"
                            data-testid={`distributor-slate-window-remove-${i}`}>
                      <Trash2 size={12}/>
                    </button>
                  )}
                </div>
                <div className="grid sm:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-[9px] uppercase tracking-[0.18em] text-white/50">Window Kind</Label>
                    <select value={w.kind} onChange={(e) => updWindow(i, "kind", e.target.value)}
                            className="mt-1 w-full h-9 px-2 rounded-md bg-black/40 border border-white/15 text-xs text-white"
                            data-testid={`distributor-slate-window-kind-${i}`}>
                      {Object.entries(WINDOW_LABELS).map(([k, l]) => (
                        <option key={k} value={k}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[9px] uppercase tracking-[0.18em] text-white/50">Territory</Label>
                    <Input value={w.territory} onChange={(e) => updWindow(i, "territory", e.target.value)}
                           placeholder="India / GCC / Overseas…"
                           className="mt-1 bg-black/40 border-white/15 h-9 text-sm"
                           data-testid={`distributor-slate-window-territory-${i}`}/>
                  </div>
                  <div>
                    <Label className="text-[9px] uppercase tracking-[0.18em] text-white/50">Start Date</Label>
                    <Input value={w.start_date} onChange={(e) => updWindow(i, "start_date", e.target.value)}
                           type="date"
                           className="mt-1 bg-black/40 border-white/15 h-9 text-sm"
                           data-testid={`distributor-slate-window-start-${i}`}/>
                  </div>
                  <div>
                    <Label className="text-[9px] uppercase tracking-[0.18em] text-white/50">End Date</Label>
                    <Input value={w.end_date} onChange={(e) => updWindow(i, "end_date", e.target.value)}
                           type="date"
                           className="mt-1 bg-black/40 border-white/15 h-9 text-sm"
                           data-testid={`distributor-slate-window-end-${i}`}/>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={() => updWindow(i, "exclusive", !w.exclusive)}
                          className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase transition flex items-center gap-1 ${
                            w.exclusive ? "bg-emerald-500/25 text-emerald-200" : "bg-white/8 text-white/55"
                          }`}
                          data-testid={`distributor-slate-window-exclusive-${i}`}>
                    {w.exclusive && <Check size={10}/>} Exclusive
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost" onClick={onClose}
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5">
            Cancel
          </Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="distributor-slate-save">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Saving…</> : "Save Film"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
